(() => {
  'use strict';
  const dialog = document.querySelector('[data-nh-cart-drawer]');
  if (!document.body.classList.contains('nh-retail') || !dialog || typeof dialog.showModal !== 'function') return;
  const root = dialog.dataset.root || '/';
  const status = dialog.querySelector('[data-drawer-status]');
  let busy = false;
  let uncertain = false;
  let pending;
  let queuedRefresh;
  let previousOverflow;
  let returnFocus;
  let shippingStart;
  function animateShipping(previous) {
    const fill = dialog.querySelector('[data-shipping-fill]');
    const progress = dialog.querySelector('[data-drawer-shipping] progress');
    if (!fill || !progress || !fill.animate || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const end = Math.max(0, Math.min(100, Number(progress.value))) / 100;
    const start = Math.max(0, Math.min(100, Number(previous) || 0)) / 100;
    fill.animate([{ transform: `scaleX(${start})` }, { transform: `scaleX(${end})` }], { duration: 650, easing: 'cubic-bezier(.22,1,.36,1)' });
  }
  const request = async (url, options = {}) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch(url, { credentials: 'same-origin', ...options, signal: controller.signal });
      if (!response.ok) throw new Error('Cart request failed');
      return await response.json();
    } finally { clearTimeout(timer); }
  };
  function lock(value) {
    busy = value;
    dialog.setAttribute('aria-busy', String(value));
    dialog.querySelectorAll('[data-drawer-content] button, [data-drawer-content] input, [data-drawer-content] select').forEach(control => {
      control.disabled = value || uncertain;
    });
    dialog.querySelectorAll('[data-drawer-variant]').forEach(select => syncVariant(select));
  }
  function syncVariant(select) {
    const card = select.closest('[data-drawer-upsell]');
    const option = select.selectedOptions[0];
    const button = card.querySelector('[data-drawer-add]');
    const valid = option && !option.disabled && Number.isSafeInteger(Number(option.value)) && Number(option.value) > 0 && Number.isSafeInteger(Number(option.dataset.min)) && Number(option.dataset.min) > 0;
    button.disabled = busy || uncertain || !valid;
    if (!valid) return;
    button.dataset.drawerAdd = option.value;
    button.dataset.addQuantity = option.dataset.min || '1';
    card.querySelector('[data-drawer-variant-price]').textContent = option.dataset.price;
    card.querySelector('[data-drawer-variant-title]').textContent = option.dataset.title;
    const minimum = Number(button.dataset.addQuantity);
    const minimumLabel = card.querySelector('[data-drawer-variant-min]');
    minimumLabel.textContent = `Mindst ${minimum} stk.`;
    minimumLabel.hidden = minimum <= 1;
    card.querySelector('[data-drawer-variant-link]').href = option.dataset.url;
    const imageLink = card.querySelector('[data-drawer-variant-image]');
    imageLink.href = option.dataset.url;
    imageLink.hidden = !option.dataset.image;
    const image = imageLink.querySelector('img');
    image.removeAttribute('srcset');
    if (option.dataset.image) image.src = option.dataset.image;
    else image.removeAttribute('src');
  }
  function enhanceSuggestions() {
    dialog.querySelectorAll('[data-drawer-enhanced]').forEach(node => { node.hidden = false; });
    dialog.querySelectorAll('[data-drawer-fallback]').forEach(node => { node.hidden = true; });
    dialog.querySelectorAll('[data-drawer-variant]').forEach(select => syncVariant(select));
  }
  function fail(mutation) {
    status.textContent = mutation ? 'Ændringen kunne ikke bekræftes. Kontrollér kurven, før du prøver igen.' : 'Kurven kunne ikke opdateres. Åbn hele kurven for at fortsætte.';
  }
  async function render() {
    const url = new URL(root, location.origin);
    url.searchParams.set('sections', 'nh-cart-drawer');
    const sections = await request(url);
    const html = sections['nh-cart-drawer'];
    if (typeof html !== 'string') throw new Error('Missing cart section');
    const content = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-drawer-content]');
    if (!content) throw new Error('Missing cart content');
    const acceptedTerms = dialog.querySelector('.nh-cart-drawer__terms input')?.checked;
    const bodyScroll = dialog.querySelector('.nh-cart-drawer__body')?.scrollTop || 0;
    const previousShipping = shippingStart ?? dialog.querySelector('[data-drawer-shipping] progress')?.value ?? 0;
    shippingStart = undefined;
    dialog.querySelector('[data-drawer-content]').replaceWith(content);
    animateShipping(previousShipping);
    enhanceSuggestions();
    const terms = dialog.querySelector('.nh-cart-drawer__terms input');
    if (terms && acceptedTerms) terms.checked = true;
    const body = dialog.querySelector('.nh-cart-drawer__body');
    if (body) body.scrollTop = bodyScroll;
    // Navigation already listens to this event; a direct refresh also starts a duplicate request.
    document.dispatchEvent(new CustomEvent('nh:cart-updated', { detail: { item_count: Number(content.dataset.count), total_price: Number(content.dataset.total) } }));
  }
  function refresh() {
    // A separate confirmed add may finish while a drawer request is in flight.
    // Queue one fresh read afterwards rather than reuse its potentially stale HTML.
    if (pending) {
      if (!queuedRefresh) queuedRefresh = pending.then(() => {
        queuedRefresh = null;
        return refresh();
      });
      return queuedRefresh;
    }
    lock(true);
    status.textContent = 'Opdaterer kurven…';
    pending = render().then(() => { uncertain = false; status.textContent = ''; return true; }).catch(() => { uncertain = true; fail(false); return false; }).finally(() => { pending = null; lock(false); });
    return pending;
  }
  async function open(options = {}) {
    const trigger = options.trigger || document.activeElement;
    document.querySelectorAll('nh-navigation').forEach(nav => nav.closeAll?.(false));
    if (!dialog.open) {
      shippingStart = 0;
      returnFocus = trigger;
      dialog.showModal();
      previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
    }
    return refresh();
  }
  dialog.addEventListener('close', () => {
    if (previousOverflow !== undefined) document.documentElement.style.overflow = previousOverflow;
    previousOverflow = undefined;
    if (returnFocus?.isConnected && !returnFocus.disabled && typeof returnFocus.focus === 'function') {
      returnFocus.focus({ preventScroll: true });
    }
    returnFocus = undefined;
  });
  async function mutate(endpoint, payload, focusKey) {
    if (busy || uncertain) return;
    lock(true); status.textContent = 'Opdaterer kurven…';
    // A timed-out POST may have succeeded: never repeat it automatically.
    let confirmed = false;
    pending = (async () => {
      try {
        await request(root + endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(payload) });
        confirmed = true;
        await render();
        status.textContent = 'Kurven er opdateret.';
      } catch (_) {
        uncertain = true;
        // Reconcile read-only, but keep purchasing locked until a deliberate refresh or full cart visit.
        try { await render(); } catch (_) { /* Existing cart link remains available. */ }
        fail(!confirmed);
      } finally {
        pending = null; lock(false);
        const line = [...dialog.querySelectorAll('[data-line-key]')].find(node => node.dataset.lineKey === focusKey);
        const target = line?.querySelector('[data-drawer-quantity]');
        if (target && !target.disabled) target.focus();
        else if (dialog.open) dialog.querySelector('[data-drawer-close]').focus();
      }
    })();
    await pending;
  }
  dialog.addEventListener('click', event => {
    if (event.target.closest('[data-drawer-close]')) dialog.close();
    if (event.target === dialog) {
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
    }
    const remove = event.target.closest('[data-drawer-remove]');
    if (remove) { const key = remove.closest('[data-line-key]').dataset.lineKey; void mutate('cart/change.js', { id: key, quantity: 0 }, key); }
    const add = event.target.closest('[data-drawer-add]');
    if (add && !add.disabled) void mutate('cart/add.js', { items: [{ id: Number(add.dataset.drawerAdd), quantity: Number(add.dataset.addQuantity) }] });
  });
  dialog.addEventListener('change', event => {
    const select = event.target.closest('[data-drawer-variant]');
    if (select) { if (!busy && !uncertain) syncVariant(select); return; }
    const input = event.target.closest('[data-drawer-quantity]');
    if (!input || busy || uncertain) return;
    if (!input.value.trim() || !Number.isSafeInteger(Number(input.value)) || !input.checkValidity()) {
      input.value = input.defaultValue;
      status.textContent = 'Antallet blev ikke ændret. Indtast et gyldigt antal, eller brug Fjern for at fjerne varen.';
      return;
    }
    const key = input.closest('[data-line-key]').dataset.lineKey;
    void mutate('cart/change.js', { id: key, quantity: Number(input.value) }, key);
  });
  dialog.addEventListener('submit', event => {
    if (busy || uncertain || event.submitter?.name !== 'checkout') event.preventDefault();
  });
  document.addEventListener('click', event => {
    const link = event.target.closest('[data-nh-cart]');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target === '_blank') return;
    event.preventDefault(); void open({ trigger: link });
  });
  document.addEventListener('nh:cart-open', event => { void open(event.detail || {}); });
  window.NHCartDrawer = { open, refresh };
  enhanceSuggestions();
})();
